/* components/LoadMoreStatic.ts

    A simple component that targets a node, duplicates it, and replaces
    parts of its content as specified by supplied data.

    Usage example:

    <script id="story-row-data">
        [
        {% for block in section.blocks %}
            {
                // Regular elements use the class as the key
                "card-story__title": "{{ block.settings.title | escape }}",
                "card-story__text": "{{ block.settings.text | escape }}",
                // Images are targeted by using a `src-` prefix folloed by the image class
                "src-image-comparison__before": "{{ block.settings.before_image | image_url: width: 100 | escape }}",
                "src-image-comparison__after": "{{ block.settings.after_image | image_url: width: 100 | escape }}"
            }
            {% if forloop.last == false %},{% endif %}
        {% endfor %}
        ]
    </script>


    // `target` is the DOM selector that we target to inject the newly loaded items
    // `src` is the DOM selector that we use as base element to clone
    <load-more-static
        class="container"
        target=".grid"
        src=".card--story"
        dataid="#story-row-data"
        increment=3
    >

    // You must ensure that the data you are passing suits the pre-existing element you are targeting.
    // i.e. ensure images are the correct size/aspect ratio and text is of a suitable length

*/

import { Component, Ref, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';

@Component
export class LoadMoreStatic extends ThemeComponent {

    @Ref({ required: false }) button!: HTMLButtonElement;
    @Prop({ default: null }) src!: string;
    @Prop({ default: null }) target!: string;
    @Prop({ default: null }) dataid!: string;
    @Prop({ default: null }) increment!: number;

    private srcElement: HTMLElement | null;
    private targetElement: HTMLElement | null;
    private data: string | undefined;
    private dataArray: LoadStaticObject[] | undefined;
    private loadedIndex: number;
    private filter: string|null;
    private filteredItems: LoadStaticObject[]|null;

    constructor() {
        super();
        this.srcElement = null;
        this.targetElement = null;
        this.data = undefined;
        this.dataArray = undefined;
        this.loadedIndex = 2;
        this.filter = null;
        this.filteredItems = null;
    }

    init() {
        this.targetElement = this.querySelector(this.target);
        this.srcElement = this.querySelector(this.src);

        if (this.dataid) {
            const dataElement: HTMLElement | null = document.querySelector(this.dataid);
            this.data = dataElement?.innerText;
        }

        if (this.data) {
            this.dataArray = JSON.parse(this.data);
            if (this.dataArray) {
                this.filteredItems = this.dataArray;
            }
        }

        if (this.button) {
            this.button.addEventListener('click', () => {
                // this.showLoadingState();
                void this.loadMore();
            });
        }

        if (this.filteredItems && this.filteredItems.length) {
            // Show/hide the load more button
            if (this.filteredItems.length > this.loadedIndex) {
                this.button.classList.remove('visually-hidden');
            } else {
                this.button.classList.add('visually-hidden');
            }
        }
    }

    async loadMore() {

        if (this.filteredItems && this.filteredItems.length) {

            // When the load more button is clicked, we get
            // the next three items in the filtered data
            const itemArray = this.filteredItems?.slice(this.loadedIndex, this.loadedIndex + 2);

            // Show/hide the load more button
            if (this.loadedIndex + 2 >= this.filteredItems.length) {
                this.button.classList.add('visually-hidden');
            } else {
                this.button.classList.remove('visually-hidden');
            }

            if (itemArray) {

                const imagePromises = [];
                const imageElements:HTMLElement[] = [];

                for (const object of itemArray) {

                    const elementsToChange = object.elements;

                    let cloneElement: HTMLElement | null = null;
                    let card: HTMLElement | null = null;

                    if (elementsToChange[0].value === 'slider') {
                        card = document.querySelector('.load-more-static__grid-item--sliding');

                        if (!card) {
                            const svgObject = elementsToChange.find((element) => element.key === 'svg');
                            const svgUrl = svgObject ? svgObject.value : '';
                            const elementStr = this.slidingCard(svgUrl);

                            const parser = new DOMParser();
                            const doc = parser.parseFromString(elementStr, 'text/html');
                            cloneElement = doc.body.firstChild as HTMLElement | null;

                        }else {
                            cloneElement = card?.cloneNode(true) as HTMLElement;
                        }

                    }else {
                        card = document.querySelector('.load-more-static__grid-item--non-sliding');

                        if (!card) {
                            const elementStr = this.nonSlidingCard();

                            const parser = new DOMParser();
                            const doc = parser.parseFromString(elementStr, 'text/html');
                            cloneElement = doc.body.firstChild as HTMLElement | null;
                        }else {
                            cloneElement = card?.cloneNode(true) as HTMLElement;
                        }
                    }

                    if (!cloneElement) {
                        return;
                    }

                    for (const change of elementsToChange) {
                        if (change.key.includes('src-')) {
                            const keyClass = change.key.replace('src-', '');
                            let element = cloneElement.querySelector<HTMLImageElement>(`.${keyClass}`);

                            if (element && element.tagName !== 'IMG') {
                                const imageElement = element.querySelector('img');
                                if (imageElement) {
                                    element = imageElement;
                                } else {
                                    // eslint-disable-next-line no-console
                                    console.error('No image element found within ', element);
                                    continue;
                                }
                            }

                            // If element exists then update the images
                            if (element) {
                                element.src = "";
                                element.srcset = "";
                                imagePromises.push(this.loadImage(change.value, element));
                            }
                        } else {
                            // Otherwise simply replace the element contents
                            const element = cloneElement.querySelector(`.${change.key}`);
                            if (element) {
                                element.innerHTML = change.value;
                            }
                        }
                    }


                    cloneElement.classList.add('fade-in');
                    const container = document.createElement('div');
                    container.classList.add('load-more-static__grid-item');
                    container.appendChild(cloneElement);
                    imageElements.push(container);
                    setTimeout(() => {
                        if (cloneElement) {
                            cloneElement.classList.add('fade-in-trigger');
                        }
                    }, 200);
                }

                await Promise.all(imagePromises).then(() => {
                    // Add all elements to dom
                    for (const container of imageElements) {
                        this.targetElement?.appendChild(container);
                    }
                }).catch((err) => {
                    //
                });

                this.loadedIndex += 2;
            }

        }
    }

    async loadImage(src: string, element: HTMLImageElement) {

        const imageLoadPromise = new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve({ src });
            img.onerror = () => {
                this.$log.error('Image failed to load');
            };
            img.src = src;
            img.srcset = src;
        });

        await imageLoadPromise;
        element.src = src;
        return src;
    }

    setFilter(filterString: string) {
        if (
            this.dataArray &&
            this.dataArray.length &&
            filterString &&
            this.targetElement
        ) {
            this.filter = filterString;
            if (filterString == 'all') {
                this.filteredItems = this.dataArray;
            } else {
                this.filteredItems = this.dataArray.filter(
                    (item) => {
                        // Filter matches can be added as commma separated values
                        // for example: Hair, Skin
                        // So we convert to an array and then match for each value
                        const itemFilterArray = item.filter.split(', ');
                        // eslint-disable-next-line max-len
                        return itemFilterArray.find((filter) => filter.toLowerCase().split(' ').join('-') == filterString);
                    }
                );
            }
            this.loadedIndex = 0;
            this.targetElement.innerHTML = '';
            void this.loadMore();
        }
    }

    slidingCard(svgUrl: string) {
        return `<div class="load-more-static__grid-item load-more-static__grid-item--sliding">
                    <div class="card card--story card--sliding-image-comparison__after text-align-center">
                        <image-slider>
                            <div 
                                class="image-slider" data-ref="sliderWrapper" 
                                style="height: 468px;"
                            >
                                <div class="
                                        image-slider__image 
                                        image-slider__image-overlay 
                                        sliding-image-comparison__before
                                        " 
                                      data-ref="overlay" 
                                      style="width: 322.5px;"
                                    >
                                   <img src="" alt="Before Image" style="width: 645px; object-fit: cover;">
                                    <figcaption class="image-slider__caption image-slider__caption--left">
                                        Before
                                    </figcaption>
                                </div>
                                <div class="image-slider__slider" 
                                     data-ref="slider" 
                                     style="top: 0px; left: 320px; height: 468px;"
                                >
                                    <button 
                                        type="button" 
                                        class="image-slider__slider-button flex" 
                                        data-ref="sliderButton"
                                    >
                                    <img src="${svgUrl}" width="25px" height="25px" alt="Arrow Icon" />
                                    </button>
                                </div>
                                <div 
                                    class="image-slider__image sliding-image-comparison__after" 
                                    data-ref="overlayConstraint"
                                >
                                    <img src="" alt="After Image" style="width: 645px; object-fit: cover;">
                                    <figcaption class="image-slider__caption image-slider__caption--right">
                                        After
                                    </figcaption>
                                </div>
                            </div>
                        </image-slider>
                        <div class="card-story__title h5 mt-25"></div>
                        <div class="card-story__text mt-10 text-light fs-14"></div>
                    </div>
                </div>`;
    }

    nonSlidingCard() {
        return `<div class="load-more-static__grid-item load-more-static__grid-item--non-sliding">
            <div class="card card--story card--image-comparison__after text-align-center">
                <div>
                    <div class="grid grid--2-2-2 grid--gg-0">
                        <div class="image-with-label image-with-label--bottom">
                            <img src="" 
                                 class="image-comparison__before" 
                                 width="800" height="1200" 
                                 alt="" 
                                 loading="lazy"
                            >
                            <div class="image-with-label__label-wrapper">
                                <div class="image-with-label__label">Before</div>
                            </div>
                        </div>
                        <div class="
                                image-with-label 
                                image-with-label--bottom 
                                image-with-label--image-comparison__after
                                ">
                            <img src="" 
                                 class="image-comparison__after" 
                                 width="800" 
                                 height="1200" 
                                 alt="" 
                                 loading="lazy"
                            >  
                            <div class="image-with-label__label-wrapper">
                                <div class="image-with-label__label">After</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-story__title h5 mt-25">
                </div>
                <div class="card-story__text mt-10 text-light fs-14">
                </div>
            </div>
        </div>`;
    }
}

interface LoadStaticObject {
    "elements": [
        {
            "key": string;
            "value": string;
        }
    ];
    "filter": string;
}
